import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';

import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';
import { activePeriod, disabledDate } from '../../utils/date';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
  addOrGotoPayslipTab,
} from '../../store/reducers/payrollSlice';
import { PAYSLIP_LIST_FIELDS } from '../../apollo/fragments';
import {
  GENERATE_PAYSLIP_MUTATION,
  REFRESH_PAYSLIP_MUTATION,
} from '../../apollo/mutations';
import { LOCATIONS_OPTIONS_QUERY } from '../../apollo/queries';

import GeneratePayslipForm from '../../components/Payroll/Forms/GeneratePayslipForm';
import ProfileCard from '../../components/Employee/ProfileCard';

const PAYSLIPS_QUERY = gql`
  ${PAYSLIP_LIST_FIELDS}
  query GetPayslips(
    $first: Int
    $page: Int
    $filters: [QueryGetPayslipsFilter]
    $sort: QueryGetPayslipsSorter
  ) {
    payslips(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...PayslipListFields
      }
    }
  }
`;

const Payslips = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.payroll);
  const { user } = useSelector((state) => state.login);
  const { ability } = user;
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showGeneratePayslipModal, setShowGeneratePayslipModal] = useState(
    false
  );
  const [generatePayslipErrors, setGeneratePayslipErrors] = useState('');

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(PAYSLIPS_QUERY, {
    variables: queryVariables,
  });

  const { loading: loadingL, data: locations } = useQuery(
    LOCATIONS_OPTIONS_QUERY,
    {
      variables: {
        userId: user.id,
      },
    }
  );

  console.log('ERROR', error);

  const [generatePayslip, { loading: generatingPayslip }] = useMutation(
    GENERATE_PAYSLIP_MUTATION
  );

  const [refreshPayslipById, { loading: refreshingPayslipById }] = useMutation(
    REFRESH_PAYSLIP_MUTATION
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const onPayslipClick = (payslip) => {
    console.log('onPayslipClick///', payslip);
    const tab = {
      title: payslip.id,
      key: `payslip-${payslip.id}`,
      data: payslip.id,
      closable: true,
    };
    dispatch(addOrGotoPayslipTab({ tab }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-75',
      render: (value, record) => {
        return (
          <Button
            type="link"
            onClick={() => onPayslipClick(record)}
            style={{ padding: '0' }}
          >
            {value}
          </Button>
        );
      },
    },
    {
      title: 'EMP ID',
      dataIndex: 'emp_id',
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-150',
      render: (value, record) => {
        return <ProfileCard employee={record} />;
      },
    },
    {
      title: 'For Month',
      dataIndex: 'for_month',
      key: 'for_month',
      serverKey: 'MONTH',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'month',
      className: 'width-150',
    },
    {
      title: 'Conveyance',
      dataIndex: 'conveyance',
      key: 'conveyance',
      serverKey: 'CONVEYANCE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'FE Incentive',
      dataIndex: 'fe_incentive_amount',
      key: 'fe_incentive_amount',
      serverKey: 'FE_INCENTIVE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'TL Incentive',
      dataIndex: 'tl_incentive_amount',
      key: 'tl_incentive_amount',
      serverKey: 'TL_INCENTIVE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Allowance',
      dataIndex: 'allowance',
      key: 'allowance',
      serverKey: 'ALLOWANCE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Deduction',
      dataIndex: 'deduction',
      key: 'deduction',
      serverKey: 'DEDUCTION',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      // className: 'width-150',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'date',
      // className: 'width-150',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-75',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          freezePrevMonth,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            {ability.toListPayslips && (
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                title="View"
                onClick={() => onPayslipClick(record)}
                disabled={isEditable}
              ></Button>
            )}
            {ability.toGeneratePayslips && (
              <Popconfirm
                title="Are you sure to refresh this payslip?"
                onConfirm={() => {
                  onPayslipRefreshSubmit(record.id);
                }}
                onCancel={() => {}}
                okText="Refresh"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<ReloadOutlined />}
                  title="Refresh"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // const columnsForExport = [...columns];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onGeneratePayslipSubmit = (values, callBack) => {
    console.log('onGeneratePayslipSubmit called with///', values);
    const variables = {
      input: {
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        location_id: values.location && parseInt(values.location[1]),
      },
    };
    generatePayslip({
      variables,
    })
      .then(({ data }) => {
        console.log('onGeneratePayslipSubmit finished', generatePayslip);
        if (generatePayslip) {
          callBack({ status: true });
          onSuccessMutation('Payslip generated successfully!');
          setShowGeneratePayslipModal(false);
          setGeneratePayslipErrors(null);
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = '';
        if (e.graphQLErrors) {
          e.graphQLErrors.forEach(({ extensions, message: errorMessage }) => {
            const { category, reason } = extensions;
            if (category === 'custom' && reason === 'Validation') {
              setGeneratePayslipErrors(errorMessage);
            } else {
              error = errorMessage;
              message.error(
                error ? error : 'Some error occured, Please try again !!!'
              );
            }
          });
        }
        callBack({ status: false });
      });
  };

  const onPayslipRefreshSubmit = (id) => {
    console.log('onPayslipRefreshSubmit called with///', id);
    const variables = {
      id: id,
    };
    refreshPayslipById({
      variables,
    })
      .then(({ data }) => {
        const payslip = data.refreshPayslipById;
        console.log('onPayslipRefreshSubmit finished', payslip);
        if (payslip) {
          onSuccessMutation('Payslip refreshed successfully!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onGeneratePayslipModalClose = () => {
    setShowGeneratePayslipModal(false);
  };

  return (
    <Spin spinning={loading || generatingPayslip || refreshingPayslipById}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            scroll={{ x: 1200 }}
            dataSource={data.payslips.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.payslips.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={
              ability.toDeleteAllowance &&
              (() => {
                setShowGeneratePayslipModal(true);
              })
            }
            addNewEntryBtnLabel="Generate Paylsip"
          />
        )}
      </Card>
      <Modal
        title="Generate Payslip"
        visible={showGeneratePayslipModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onGeneratePayslipModalClose}
        maskClosable={false}
      >
        <Spin spinning={generatingPayslip}>
          <GeneratePayslipForm
            locations={locations && locations.locations}
            onGeneratePayslipSubmit={onGeneratePayslipSubmit}
            onCancelClick={onGeneratePayslipModalClose}
            errors={generatePayslipErrors}
            onCloseError={() => {
              setGeneratePayslipErrors(null);
            }}
            ability={ability}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Payslips;
